import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { 
  Smartphone, 
  Laptop, 
  Monitor, 
  Home, 
  Music, 
  Plug, 
  Shield, 
  Recycle, 
  DollarSign, 
  Truck, 
  Search, 
  Lock, 
  Package, 
  Leaf,
  CheckCircle,
  ArrowRight
} from 'lucide-react';

const EWaste = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const headerRef = useRef(null);
  const categoriesRef = useRef(null);
  const statsRef = useRef(null);
  
  const headerInView = useInView(headerRef, { once: true });
  const categoriesInView = useInView(categoriesRef, { once: true });
  const statsInView = useInView(statsRef, { once: true });

  const ewasteCategories = [
    {
      id: 'mobile',
      name: 'Mobile Phones',
      icon: Smartphone,
      items: ['Smartphones', 'Feature phones', 'Tablets', 'Accessories'],
      price: '₹50 - ₹500 per piece',
      description: 'We accept all types of mobile devices and accessories',
      color: 'from-[#102018] to-[#2F6F45]'
    },
    {
      id: 'computer',
      name: 'Computers & Laptops',
      icon: Laptop,
      items: ['Desktop computers', 'Laptops', 'Monitors', 'Keyboards', 'Mice'],
      price: '₹200 - ₹2000 per piece',
      description: 'Complete computer systems and individual components',
      color: 'from-[#2F6F45] to-[#70BD7D]'
    },
    {
      id: 'tv',
      name: 'TVs & Displays',
      icon: Monitor,
      items: ['LED TVs', 'LCD TVs', 'CRT TVs', 'Computer monitors', 'Projectors'],
      price: '₹100 - ₹1500 per piece',
      description: 'All types of display devices and televisions',
      color: 'from-[#102018] to-[#2F6F45]'
    },
    {
      id: 'appliances',
      name: 'Home Appliances',
      icon: Home,
      items: ['Washing machines', 'Refrigerators', 'ACs', 'Microwaves', 'Fans'],
      price: '₹300 - ₹3000 per piece',
      description: 'Large and small home appliances',
      color: 'from-[#2F6F45] to-[#70BD7D]'
    },
    {
      id: 'audio',
      name: 'Audio Equipment',
      icon: Music,
      items: ['Speakers', 'Headphones', 'Amplifiers', 'Music players', 'Radios'],
      price: '₹50 - ₹800 per piece',
      description: 'Audio and music equipment',
      color: 'from-[#102018] to-[#2F6F45]'
    },
    {
      id: 'other',
      name: 'Other Electronics',
      icon: Plug,
      items: ['Cameras', 'Gaming consoles', 'Printers', 'Routers', 'Batteries'],
      price: '₹20 - ₹1000 per piece',
      description: 'Miscellaneous electronic items',
      color: 'from-[#2F6F45] to-[#70BD7D]'
    }
  ];

  const safetyFeatures = [
    {
      title: 'Data Destruction',
      description: 'Complete data wiping and destruction to protect your privacy',
      icon: Lock,
      color: 'from-[#102018] to-[#2F6F45]'
    },
    {
      title: 'Certified Recycling',
      description: 'Environmentally safe disposal following government guidelines',
      icon: Recycle,
      color: 'from-[#2F6F45] to-[#70BD7D]'
    },
    {
      title: 'Best Market Rates',
      description: 'Competitive pricing based on current market conditions',
      icon: DollarSign,
      color: 'from-[#102018] to-[#2F6F45]'
    },
    {
      title: 'Free Pickup',
      description: 'Convenient doorstep collection at no extra cost',
      icon: Truck,
      color: 'from-[#2F6F45] to-[#70BD7D]'
    }
  ];

  const processSteps = [
    {
      step: 1,
      title: 'Assessment',
      description: 'Our expert evaluates your e-waste items and provides accurate pricing',
      icon: Search
    },
    {
      step: 2,
      title: 'Data Security',
      description: 'We ensure complete data destruction from all storage devices',
      icon: Shield
    },
    {
      step: 3,
      title: 'Safe Collection',
      description: 'Careful handling and transportation of your e-waste items',
      icon: Package
    },
    {
      step: 4,
      title: 'Certified Disposal',
      description: 'Environmentally responsible recycling and disposal',
      icon: Leaf
    }
  ];

  const filteredCategories = selectedCategory === 'all' 
    ? ewasteCategories 
    : ewasteCategories.filter(cat => cat.id === selectedCategory);

  // Debug logging
  console.log('Selected category:', selectedCategory);
  console.log('Filtered categories:', filteredCategories);
  console.log('All categories:', ewasteCategories);

  const handleBookNow = () => {
    navigate('/book-appointment');
  };

  return (
    <div className="min-h-screen bg-[#F6F8F4]">

      {/* Header Section */}
      <section ref={headerRef} className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-8"
          >
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-[#102018] to-[#2F6F45] rounded-2xl flex items-center justify-center shadow-lg shadow-[#2F6F45]/20">
                <Laptop className="w-8 h-8 text-[#70BD7D]" />
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl font-heading font-bold text-[#102018] mb-4">
              E-Waste{' '}
              <span className="bg-gradient-to-r from-[#2F6F45] to-[#70BD7D] bg-clip-text text-transparent">
                Collection & Recycling
              </span>
            </h1>

            <p className="text-xl text-[#718078] max-w-3xl mx-auto leading-relaxed">
              Responsible disposal of electronic waste with data security and environmental protection. 
              Get the best rates while ensuring your data is completely secure.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl font-heading font-bold text-[#102018] mb-6">
              Choose Your{' '}
              <span className="bg-gradient-to-r from-[#2F6F45] to-[#70BD7D] bg-clip-text text-transparent">
                E-Waste Category
              </span>
            </h2>
            
            <div className="flex flex-wrap justify-center gap-4 mb-8">

              <motion.button
                onClick={() => setSelectedCategory('all')}
                className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                  selectedCategory === 'all'
                    ? 'bg-gradient-to-r from-[#102018] to-[#2F6F45] text-white shadow-lg shadow-[#2F6F45]/20'
                    : 'bg-white text-[#718078] hover:bg-[#EAF5EC] hover:text-[#2F6F45] border border-[#DDE8DF]'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                All Categories
              </motion.button>

              {ewasteCategories.map(category => (
                <motion.button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center space-x-2 ${
                    selectedCategory === category.id
                      ? 'bg-gradient-to-r from-[#102018] to-[#2F6F45] text-white shadow-lg shadow-[#2F6F45]/20'
                      : 'bg-white text-[#718078] hover:bg-[#EAF5EC] hover:text-[#2F6F45] border border-[#DDE8DF]'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <category.icon className="w-5 h-5" />
                  <span>{category.name}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>

          {/* Categories Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCategories.length === 0 ? (
              <div className="col-span-full text-center py-8">
                <p className="text-[#718078]">
                  No categories found. Please try selecting a different filter.
                </p>
              </div>
            ) : (
              filteredCategories.map((category, index) => (
                <motion.div
                  key={category.id}
                  className="card-hover p-6 bg-white border border-[#DDE8DF]"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-center mb-4">
                    <div
                      className={`w-12 h-12 rounded-xl bg-gradient-to-r ${category.color} flex items-center justify-center mr-4`}
                    >
                      <category.icon className="w-6 h-6 text-white" />
                    </div>

                    <div>
                      <h3 className="text-lg font-heading font-semibold text-[#102018]">
                        {category.name}
                      </h3>

                      <div className="text-sm font-medium text-[#2F6F45]">
                        {category.price}
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-[#718078] mb-4 text-sm leading-relaxed">
                    {category.description}
                  </p>
                  
                  <div>
                    <h4 className="text-sm font-medium text-[#102018] mb-2">
                      We Accept:
                    </h4>

                    <ul className="space-y-1">
                      {category.items.map((item, itemIndex) => (
                        <li
                          key={itemIndex}
                          className="flex items-center space-x-2 text-sm text-[#718078]"
                        >
                          <CheckCircle className="w-3 h-3 text-[#2F6F45]" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Safety Features */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container-custom">

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-heading font-bold text-[#102018] mb-4">
              Why Choose Our{' '}
              <span className="bg-gradient-to-r from-[#2F6F45] to-[#70BD7D] bg-clip-text text-transparent">
                E-Waste Service?
              </span>
            </h2>

            <p className="text-[#718078] max-w-2xl mx-auto">
              We provide secure, environmentally responsible e-waste disposal with complete data protection
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {safetyFeatures.map((feature, index) => (
              <motion.div
                key={index}
                className="card text-center bg-white border border-[#DDE8DF]"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <div
                  className={`w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-r ${feature.color} flex items-center justify-center`}
                >
                  <feature.icon className="w-8 h-8 text-white" />
                </div>

                <h3 className="text-xl font-heading font-semibold text-[#102018] mb-3">
                  {feature.title}
                </h3>

                <p className="text-[#718078] leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="container-custom">

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-heading font-bold text-[#102018] mb-4">
              Our E-Waste{' '}
              <span className="bg-gradient-to-r from-[#2F6F45] to-[#70BD7D] bg-clip-text text-transparent">
                Process
              </span>
            </h2>

            <p className="text-[#718078] max-w-2xl mx-auto">
              Simple, secure, and environmentally responsible e-waste disposal process
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.step}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="relative mb-6">

                  <div className="w-20 h-20 mx-auto bg-gradient-to-r from-[#102018] to-[#2F6F45] rounded-2xl flex items-center justify-center mb-4 shadow-lg shadow-[#2F6F45]/15">
                    <step.icon className="w-10 h-10 text-[#70BD7D]" />
                  </div>

                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#70BD7D] text-[#102018] rounded-full flex items-center justify-center text-sm font-bold">
                    {step.step}
                  </div>

                </div>

                <h3 className="text-lg font-heading font-semibold text-[#102018] mb-3">
                  {step.title}
                </h3>

                <p className="text-[#718078] text-sm leading-relaxed">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Environmental Impact Stats */}
      <section
        ref={statsRef}
        className="py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#102018] to-[#2F6F45] text-white"
      >
        <div className="container-custom">

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={statsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-heading font-bold mb-4">
              Environmental Impact
            </h2>

            <p className="text-[#EAF5EC]">
              Making a difference through responsible e-waste management
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">

            {[
              { number: '95%', label: 'Recycling Rate', icon: Recycle },
              { number: '10,000+', label: 'Devices Recycled', icon: Laptop },
              { number: '100%', label: 'Data Security', icon: Shield },
              { number: '24/7', label: 'Support', icon: CheckCircle }
            ].map((stat, index) => (
              <motion.div
                key={index}
                className="text-center p-8 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={statsInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                whileHover={{ scale: 1.05 }}
              >
                <stat.icon className="w-12 h-12 mx-auto mb-4 text-[#70BD7D]" />

                <div className="text-4xl font-heading font-bold mb-2">
                  {stat.number}
                </div>

                <p className="text-[#EAF5EC] font-medium">
                  {stat.label}
                </p>
              </motion.div>
            ))}

          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container-custom">

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-heading font-bold text-[#102018] mb-4">
              Frequently Asked{' '}
              <span className="bg-gradient-to-r from-[#2F6F45] to-[#70BD7D] bg-clip-text text-transparent">
                Questions
              </span>
            </h2>

            <p className="text-[#718078]">
              Everything you need to know about our e-waste services
            </p>
          </motion.div>
          
          <div className="max-w-3xl mx-auto space-y-4">

            {[
              {
                question: 'Is my data safe?',
                answer: 'Yes, we provide complete data destruction services. All storage devices are securely wiped or physically destroyed to ensure your data cannot be recovered.'
              },
              {
                question: 'What if my device is damaged?',
                answer: 'We accept all electronic devices regardless of their condition. Even damaged or non-functional devices have value for recycling.'
              },
              {
                question: 'How do you determine the price?',
                answer: 'Pricing depends on the type of device, its condition, age, and current market rates for precious metals and components.'
              },
              {
                question: 'Do you provide certificates?',
                answer: 'Yes, we provide proper documentation and certificates for e-waste disposal as required by environmental regulations.'
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                className="card bg-white border border-[#DDE8DF]"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <details className="group">

                  <summary className="flex items-center justify-between cursor-pointer text-lg font-heading font-semibold text-[#102018] hover:text-[#2F6F45] transition-colors duration-200">

                    <span>{faq.question}</span>

                    <div className="w-6 h-6 rounded-full bg-[#EAF5EC] flex items-center justify-center group-open:rotate-180 transition-transform duration-200">

                      <svg
                        className="w-4 h-4 text-[#2F6F45]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>

                    </div>

                  </summary>

                  <div className="mt-4 pt-4 border-t border-[#DDE8DF]">
                    <p className="text-[#718078] leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>

                </details>
              </motion.div>
            ))}

          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#2F6F45] to-[#102018] text-white">
        <div className="container-custom">

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >

            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
              Ready to Dispose Your E-Waste?
            </h2>

            <p className="text-xl text-[#EAF5EC] mb-8 max-w-2xl mx-auto">
              Get the best rates for your electronic waste while ensuring environmental safety and data security
            </p>

            <motion.button
              onClick={handleBookNow}
              className="btn-primary text-lg px-8 py-4 flex items-center space-x-2 mx-auto bg-[#70BD7D] text-[#102018] hover:bg-white"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span>Book E-Waste Pickup</span>
              <ArrowRight className="w-5 h-5" />
            </motion.button>

          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default EWaste;